import { useMemo, useRef, useState } from 'react';
import type { FieldRendererProps } from '../../config/types';
import { Input } from '@/components/ui/Input';

/**
 * ENT-400 — NIC sub-class picker (MCA SPICe+ "Main NIC Code (SUB-CLASS)").
 *
 * ## Why this is a suggesting text input and NOT a `select`
 *
 * On the 2026-08-29 live run, prefill submitted `6201` — a NIC *group*, not a *sub-class* —
 * so MCA's search matched nothing and both Main NIC Code and Description stayed EMPTY. The
 * operator had to search the portal manually, mid-filing. A plain text box cannot tell a
 * client which of ~1,500 codes is theirs; that is what this fixes.
 *
 * But the option list is a SUGGESTION SET, not an allowlist. It is derived (see
 * entity-setup-svc `nic-catalogue.ts`) as NIC-2008 five-digit sub-classes ∪ NIC-2008
 * four-digit classes padded with a trailing `0`. That rule reproduces every code MCA has
 * been observed to return, and yields 1,547 against MCA's own observed count of 1,541 —
 * close enough to corroborate, NOT to prove. MCA cannot be enumerated to close the gap.
 *
 * So a handful of codes MCA accepts may be missing here. A `select` would turn each of
 * those into a hard intake failure for a real client; free typing keeps the pre-existing
 * behaviour as the floor and adds assistance on top. The backend keeps `nic_code` as a
 * text input with a `^[0-9]{5}$` rule for exactly the same reason — do not "tidy" this
 * into a select to match the other option-bearing fields.
 */
export function NicPickerField({ field, value, onChange, onBlur, error }: FieldRendererProps) {
  const current = (value as string) ?? '';
  const [query, setQuery] = useState(current);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const options = useMemo(
    () => (field.dataSource?.type === 'static' ? field.dataSource.options : []),
    [field.dataSource],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return [];
    // Match on the code prefix OR anywhere in the description: a client searches by what
    // they DO ("tax consultancy"), not by a number they do not yet know.
    return options
      .filter((o) => o.value.startsWith(q) || o.label.toLowerCase().includes(q))
      .slice(0, 20);
  }, [options, query]);

  const commit = (code: string) => {
    onChange(code);
    setQuery(code);
    setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        type="text"
        id={field.id}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          // Free text is authoritative — typing an unlisted code must still submit.
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Defer so a click on a suggestion lands before the list unmounts.
          blurTimer.current = setTimeout(() => {
            setOpen(false);
            onBlur?.();
          }, 120);
        }}
        placeholder={field.placeholder ?? 'Search by code or activity, e.g. 62010 or "tax consultancy"'}
        disabled={field.isDisabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open && matches.length > 0}
        aria-autocomplete="list"
        aria-controls={`${field.id}-nic-options`}
        aria-invalid={!!error}
      />
      {open && matches.length > 0 && (
        <ul
          id={`${field.id}-nic-options`}
          role="listbox"
          // Design-system tokens, NOT raw greys with `dark:` variants. This app has no
          // dark mode: there is no `darkMode` key in the Tailwind config and nothing ever
          // puts a `dark` class on an ancestor, so `dark:` resolves to the
          // prefers-color-scheme MEDIA query and fires off the VIEWER'S OS setting. The
          // surrounding page keeps using the app's own light tokens, so on any machine
          // with the OS in dark mode this list painted near-black while its text stayed
          // dark — reported illegible on dev, 2026-08-31. Matches PhoneField's dropdown.
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-surface-elevated shadow-ambient"
        >
          {matches.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === current}
                className="block w-full px-3 py-2 text-left text-sm text-text hover:bg-surface-muted"
                onMouseDown={() => {
                  // onMouseDown, not onClick — it fires before the input's blur.
                  if (blurTimer.current) clearTimeout(blurTimer.current);
                  commit(o.value);
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function NicPickerDisplay({ value }: { value: unknown }) {
  return <span>{(value as string) || '—'}</span>;
}
